import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import ConfirmationDialog from "@/components/ConfirmationDialog"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfQuery, useUpdatePatientProfileMutation } from "@/data/queries"
import AddMedication from "@/screens/onboarding/components/AddMedication"
import { useUnsavedChangesGuard } from "@/util/hooks/useUnsavedChangesGuard"
import { useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

type Medication = { name: string }

const sameMedications = (a: Medication[], b: Medication[]) => {
  if (a.length !== b.length) return false
  return a.every((medication, index) => medication.name === b[index]?.name)
}

type UpdateMedicationsFormProps = {
  patientId: string
  initialMedications: Medication[]
}

// Split out so `initialMedications` seeds useState on mount only — later
// useSelfQuery refetches must not clobber in-progress edits.
const UpdateMedicationsForm = ({ patientId, initialMedications }: UpdateMedicationsFormProps) => {
  const navigate = useNavigate()
  const { mutateAsync: updateProfile, isPending } = useUpdatePatientProfileMutation()

  const [medications, setMedications] = useState<Medication[]>(initialMedications)
  const [savedMedications, setSavedMedications] = useState<Medication[]>(initialMedications)

  const isDirty = !sameMedications(medications, savedMedications)

  const { isPromptOpen, guard, confirm, cancel } = useUnsavedChangesGuard(isDirty)

  const handleSubmit = async () => {
    try {
      await updateProfile({
        patientId,
        profile: { medications },
      })
      setSavedMedications(medications)
      toast.success("Medications saved.")
    } catch {
      toast.error("Failed to save medications. Please try again.")
    }
  }

  return (
    <>
      <div className="flex min-h-[calc(100vh-56px)] flex-col justify-between">
        <div className="flex flex-col gap-6">
          <BackButton customClickHandler={() => guard(() => navigate(-1))} />

          <h1 className="text-h2 font-bold text-primary-contrast">Medication(s):</h1>

          <AddMedication medications={medications} onChange={setMedications} />
        </div>

        <Button
          type="button"
          disabled={isPending}
          className="flex w-full gap-3 py-2 text-base"
          onClick={handleSubmit}
        >
          {isPending && <Spinner />}
          Update
        </Button>
      </div>

      <ConfirmationDialog
        open={isPromptOpen}
        title="Unsaved Changes"
        description="You have unsaved changes that will be lost. Do you wish to proceed?"
        onConfirm={confirm}
        onCancel={cancel}
      />
    </>
  )
}

const UpdateMedicationsPage = () => {
  const { data: self } = useSelfQuery()

  return (
    <Page showAppbar={false}>
      {self === undefined ? (
        <div className="flex flex-col gap-6">
          <BackButton />
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        </div>
      ) : (
        <UpdateMedicationsForm
          patientId={self.id}
          initialMedications={(self.medications ?? []).map((medication) => ({
            name: medication.name,
          }))}
        />
      )}
    </Page>
  )
}

export default UpdateMedicationsPage
